import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { SalesLayout } from '../components/crm/SalesLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft, Briefcase, Plus, Pencil, Trash2, Building2, MapPin, Linkedin, Mail, Phone,
} from 'lucide-react';
import { crmFetch } from '../components/crm/crmApi';
import { JobModal } from '../components/crm/JobModal';
import { CandidateModal } from '../components/crm/CandidateModal';
import { PlacementModal } from '../components/crm/PlacementModal';
import {
  PIPELINE_STAGES, jobStatusMeta, stageMeta, salaryRange, feeLabel, employmentTypeLabel,
  fmtDate, candidateName,
} from '../components/crm/constants';

const Meta = ({ label, value, muted }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
    <p className={`text-sm font-medium ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{value}</p>
  </div>
);

export default function SalesJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [candModal, setCandModal] = useState({ open: false, candidate: null, stage: 'sourced' });
  const [placementModal, setPlacementModal] = useState({ open: false, candidate: null });

  const load = useCallback(async () => {
    const res = await crmFetch(`/jobs/${id}`);
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const job = data?.job;
  const candidates = data?.candidates || [];

  const moveStage = async (candidate, stage) => {
    if (stage === candidate.stage) return;
    const res = await crmFetch(`/candidates/${candidate.id}`, { method: 'PATCH', body: JSON.stringify({ stage }) });
    if (res.success) {
      toast.success(`Moved to ${stageMeta(stage).label}`);
      if (stage === 'placement') setPlacementModal({ open: true, candidate: { ...candidate, stage } });
      load();
    } else toast.error(res.message || 'Failed to move candidate');
  };

  const deleteCandidate = async (candidate) => {
    if (!window.confirm(`Remove ${candidateName(candidate)} from this pipeline?`)) return;
    const res = await crmFetch(`/candidates/${candidate.id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Candidate removed'); load(); }
  };

  const deleteJob = async () => {
    if (!window.confirm(`Delete job "${job.title}" and all its candidates?`)) return;
    const res = await crmFetch(`/jobs/${id}`, { method: 'DELETE' });
    if (res.success) { toast.success('Job deleted'); navigate('/crm-jobs'); }
  };

  if (loading) {
    return (
      <SalesLayout>
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </SalesLayout>
    );
  }

  if (!job) {
    return (
      <SalesLayout>
        <div className="py-16 text-center text-gray-500">Job not found.</div>
      </SalesLayout>
    );
  }

  const sm = jobStatusMeta(job.status);
  const sal = salaryRange(job.salary_min, job.salary_max, job.currency);
  const activeCount = candidates.filter((c) => !['placement', 'rejected'].includes(c.stage)).length;

  return (
    <SalesLayout>
      <div data-testid="job-detail-page">
        <button onClick={() => navigate('/crm-jobs')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-4" data-testid="back-to-jobs-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Jobs
        </button>

        {/* Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <Briefcase className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl font-bold text-gray-900" data-testid="job-title-heading">{job.title}</h1>
                    <Badge className={`${sm.className} border-0`}>{sm.label}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-1.5">
                    {job.account && (
                      <Link to={`/accounts/${job.account.id}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline" data-testid="job-account-link">
                        <Building2 className="w-3.5 h-3.5" />{job.accountName}
                      </Link>
                    )}
                    {job.location && <><span className="text-gray-300">·</span><MapPin className="w-3.5 h-3.5" />{job.location}</>}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="edit-job-btn">
                  <Pencil className="w-4 h-4 mr-1.5" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="text-rose-600 hover:text-rose-700" onClick={deleteJob} data-testid="delete-job-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => setCandModal({ open: true, candidate: null, stage: 'sourced' })} data-testid="add-candidate-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Candidate
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-6 pt-5 border-t">
              <Meta label="Employment" value={employmentTypeLabel(job.employment_type)} />
              <Meta label="Seniority" value={job.seniority || '—'} />
              <Meta label="Salary" value={sal || '—'} muted={!sal} />
              <Meta label="Fee" value={feeLabel(job) || '—'} muted={!feeLabel(job)} />
              <Meta label="Target Date" value={job.target_date ? fmtDate(job.target_date) : '—'} muted={!job.target_date} />
              <Meta label="In Pipeline" value={`${activeCount} active · ${candidates.length} total`} />
            </div>

            {(job.description || job.contactName) && (
              <div className="mt-4 pt-4 border-t text-sm text-gray-600 space-y-2">
                {job.contactName && <p><span className="text-gray-400">Hiring contact:</span> <span className="font-medium text-gray-800">{job.contactName}</span></p>}
                {job.description && <p className="whitespace-pre-wrap">{job.description}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline kanban */}
        <div className="overflow-x-auto pb-2" data-testid="pipeline-board">
          <div className="flex gap-4 min-w-max">
            {PIPELINE_STAGES.map((stage) => {
              const col = candidates.filter((c) => c.stage === stage.value);
              return (
                <div key={stage.value} className="w-64 shrink-0" data-testid={`pipeline-col-${stage.value}`}>
                  <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-t-4 bg-white ${stage.col}`}>
                    <span className="text-sm font-semibold text-gray-800">{stage.label}</span>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{col.length}</span>
                  </div>
                  <div className="bg-gray-50/80 rounded-b-lg p-2 space-y-2 min-h-[120px]">
                    {col.length === 0 && <p className="text-xs text-gray-400 text-center py-6">Empty</p>}
                    {col.map((c) => (
                      <Card key={c.id} className="shadow-sm" data-testid={`candidate-card-${c.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{candidateName(c)}</p>
                              <p className="text-xs text-gray-500 truncate">{[c.current_title, c.current_company].filter(Boolean).join(' · ') || '—'}</p>
                            </div>
                            <div className="flex gap-0.5 shrink-0">
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setCandModal({ open: true, candidate: c, stage: c.stage })} data-testid={`edit-candidate-${c.id}`}>
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-500" onClick={() => deleteCandidate(c)} data-testid={`delete-candidate-${c.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-gray-400">
                            {c.email && <a href={`mailto:${c.email}`} title={c.email} className="hover:text-blue-600"><Mail className="w-3.5 h-3.5" /></a>}
                            {c.phone && <span title={c.phone}><Phone className="w-3.5 h-3.5" /></span>}
                            {c.linkedin_url && <a href={c.linkedin_url} target="_blank" rel="noreferrer" className="hover:text-blue-600"><Linkedin className="w-3.5 h-3.5" /></a>}
                          </div>
                          <Select value={c.stage} onValueChange={(v) => moveStage(c, v)}>
                            <SelectTrigger className="h-7 mt-2 text-xs" data-testid={`candidate-stage-move-${c.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {PIPELINE_STAGES.map((s) => <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <JobModal open={editOpen} onOpenChange={setEditOpen} onSaved={load} job={job} accountName={job.accountName} />
      <CandidateModal
        open={candModal.open}
        onOpenChange={(o) => setCandModal((s) => ({ ...s, open: o }))}
        onSaved={load}
        candidate={candModal.candidate}
        defaultJobId={id}
        defaultStage={candModal.stage}
      />
      <PlacementModal
        open={placementModal.open}
        onOpenChange={(o) => setPlacementModal((s) => ({ ...s, open: o }))}
        onSaved={load}
        defaultJobId={id}
        defaultJobTitle={job.title}
        defaultCandidate={placementModal.candidate}
      />
    </SalesLayout>
  );
}
